import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/Home/HomePage';
import FieldsPage from './pages/Fields/FieldsPage';
import FieldDetailPage from './pages/FieldDetail/FieldDetailPage';
import BookingPage from './pages/Booking/BookingPage';
import DashboardPage from './pages/Dashboard/DashboardPage';
import AddFieldPage from './pages/AddField/AddFieldPage';
import AuthPage from './pages/Auth/AuthPage';
import AdminPanelPage from './pages/AdminPanel/AdminPanelPage';
import ProfilePage from './pages/Profile/ProfilePage';
import { RequireOwnerApproved } from './components/RouteGuards';
import {
  DATA_CHANGED_EVENT,
  hydrateLocalDataFromSupabase,
  refreshLocalDataFromSupabase,
  subscribeToSupabaseDataChanges,
} from './data/supabaseSync';
import { refreshFieldsFromStorage } from './data/mockData';
import { flushMessageQueue } from './services/messagingApi';
import { syncSessionFromSupabase } from './data/authStore';
import { isSupabaseConfigured, supabase } from './lib/supabase';
import { rebuildPreBookedSlotsFromBookings } from './data/bookingStore';

const FavoritesPage = lazy(() => import('./pages/Favorites/FavoritesPage'));

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="text-center">
      <div className="w-10 h-10 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
      <p className="text-gray-500 text-sm">Yükleniyor...</p>
    </div>
  </div>
);

function App() {
  const [booting, setBooting] = useState(true);
  const [dataVersion, setDataVersion] = useState(0);

  useEffect(() => {
    let mounted = true;
    let intervalId: number | undefined;
    let authSubscription: { unsubscribe: () => void } | undefined;
    let dataSubscriptionCleanup: (() => void) | undefined;
    const handleDataChanged = () => {
      refreshFieldsFromStorage();
      rebuildPreBookedSlotsFromBookings();
      setDataVersion((prev) => prev + 1);
    };
    const handleVisibilityRefresh = () => {
      if (document.visibilityState === 'visible') {
        void refreshLocalDataFromSupabase();
      }
    };
    (async () => {
      try {
        await hydrateLocalDataFromSupabase();
        await syncSessionFromSupabase();
        handleDataChanged();
        await flushMessageQueue();
        intervalId = window.setInterval(() => {
          void flushMessageQueue();
        }, 30000);
        window.addEventListener(DATA_CHANGED_EVENT, handleDataChanged);
        document.addEventListener('visibilitychange', handleVisibilityRefresh);
        dataSubscriptionCleanup = subscribeToSupabaseDataChanges();
        if (isSupabaseConfigured && supabase) {
          const { data } = supabase.auth.onAuthStateChange(() => {
            void syncSessionFromSupabase();
          });
          authSubscription = data.subscription;
        }
      } finally {
        if (mounted) setBooting(false);
      }
    })();
    return () => {
      mounted = false;
      if (intervalId) window.clearInterval(intervalId);
      authSubscription?.unsubscribe();
      dataSubscriptionCleanup?.();
      window.removeEventListener(DATA_CHANGED_EVENT, handleDataChanged);
      document.removeEventListener('visibilitychange', handleVisibilityRefresh);
    };
  }, []);

  if (booting) return <PageLoader />;

  return (
    <Router>
      <div className="min-h-screen bg-transparent flex flex-col">
        <Navbar />
        <main className="flex-1">
          <Routes key={dataVersion}>
            <Route path="/" element={<HomePage />} />
            <Route path="/sahalar" element={<FieldsPage />} />
            <Route path="/saha/:id" element={<FieldDetailPage />} />
            <Route path="/rezervasyon/:id" element={<BookingPage />} />
            <Route
              path="/panel"
              element={
                <RequireOwnerApproved>
                  <DashboardPage />
                </RequireOwnerApproved>
              }
            />
            <Route
              path="/panel/saha-ekle"
              element={
                <RequireOwnerApproved>
                  <AddFieldPage />
                </RequireOwnerApproved>
              }
            />
            <Route path="/giris" element={<AuthPage />} />
            <Route path="/profil" element={<ProfilePage />} />
            <Route
              path="/favoriler"
              element={
                <Suspense fallback={<PageLoader />}>
                  <FavoritesPage />
                </Suspense>
              }
            />
            <Route path="/panel/sahip" element={<AuthPage />} />
            <Route path="/admin/giris" element={<AuthPage />} />
            <Route path="/admin/onaylar" element={<AdminPanelPage />} />
            <Route path="/admin/panel" element={<AdminPanelPage />} />
          </Routes>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
